import PhotoSwipe from 'photoswipe';

let viewer;
let opening = false;
let generation = 0;

function selectImage(gallery, item) {
    const mainLink = gallery.querySelector('[data-gallery-open]');
    const mainImage = gallery.querySelector('[data-gallery-main-image]');
    mainLink.href = item.href;
    mainImage.src = item.dataset.preview;
    if (item.dataset.srcset) {
        mainImage.srcset = item.dataset.srcset;
    } else {
        mainImage.removeAttribute('srcset');
    }
    gallery.querySelector('[data-gallery-original]').href = item.href;
    gallery.querySelectorAll('[data-gallery-item]').forEach(link => {
        link.setAttribute('aria-current', String(link === item));
    });
}

async function imageData(item) {
    let width = Number(item.dataset.width);
    let height = Number(item.dataset.height);

    // older uploads may predate dimension metadata; resolve them only on opening.
    if (!(width > 0 && height > 0)) {
        const image = new Image();
        await new Promise(resolve => {
            const finish = () => {
                clearTimeout(timeout);
                image.onload = image.onerror = null;
                resolve();
            };
            const timeout = setTimeout(finish, 10000);
            image.onload = image.onerror = finish;
            image.src = item.href;
        });
        width = image.naturalWidth;
        height = image.naturalHeight;
        if (width && height) {
            item.dataset.width = width;
            item.dataset.height = height;
        }
    }

    return {
        src: item.href,
        width: width || 1,
        height: height || 1,
        alt: item.querySelector('img')?.alt || '',
    };
}

document.addEventListener('click', async event => {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return;
    }

    const link = event.target.closest?.('[data-gallery-open], [data-gallery-item]');
    const gallery = link?.closest('[data-product-image-gallery]');
    if (!gallery) return;

    event.preventDefault();
    if (link.matches('[data-gallery-item]')) {
        selectImage(gallery, link);

        return;
    }
    if (opening || viewer) return;

    opening = true;
    const currentGeneration = ++generation;
    const labels = JSON.parse(gallery.dataset.galleryLabels);
    const status = gallery.querySelector('[data-gallery-status]');
    const items = [...gallery.querySelectorAll('[data-gallery-item]')];
    const index = Math.max(
        0,
        items.findIndex(item => item.href === link.href)
    );
    link.setAttribute('aria-busy', 'true');
    status.hidden = true;

    try {
        const dataSource = await Promise.all(items.map(imageData));
        if (currentGeneration !== generation || !link.isConnected) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const pswp = new PhotoSwipe({
            dataSource,
            index,
            mainClass: 'product-image-viewer',
            showHideAnimationType: reducedMotion ? 'none' : 'fade',
            zoomAnimationDuration: reducedMotion ? 0 : 200,
            bgOpacity: 1,
            padding: { top: 60, bottom: 20, left: 10, right: 10 },
            imageClickAction: 'zoom',
            secondaryZoomLevel: zoom => Math.max(1, zoom.fit * 2),
            maxZoomLevel: zoom => Math.max(2, zoom.fit * 4),
            wheelToZoom: true,
            closeTitle: labels.close,
            zoomTitle: labels.zoom,
            arrowPrevTitle: labels.previous,
            arrowNextTitle: labels.next,
            errorMsg: labels.error,
        });
        viewer = pswp;
        const previousOverflow = document.documentElement.style.overflow;
        pswp.on('loadComplete', ({ content }) => {
            // a slow original can finish after the metadata probe timed out.
            const image = content.element;
            if (content.data.width === 1 && image?.naturalWidth && image?.naturalHeight) {
                content.data.width = image.naturalWidth;
                content.data.height = image.naturalHeight;
                pswp.refreshSlideContent(content.index);
            }
        });
        pswp.on('uiRegister', () => {
            pswp.ui.registerElement({
                name: 'original',
                order: 8,
                isButton: true,
                tagName: 'a',
                title: labels.original,
                ariaLabel: labels.original,
                html: '<svg class="pswp__icn" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 3h7v7m0-7L10 14M10 3H3v18h18v-7"/></svg>',
                onInit: element => {
                    element.target = '_blank';
                    element.rel = 'noopener noreferrer';
                    pswp.on('change', () => {
                        element.href = pswp.currSlide.data.src;
                        selectImage(gallery, items[pswp.currIndex]);
                    });
                },
            });
        });
        pswp.on('destroy', () => {
            document.documentElement.style.overflow = previousOverflow;
            viewer = undefined;
            if (link.isConnected) link.focus({ preventScroll: true });
        });
        document.documentElement.style.overflow = 'hidden';
        pswp.init();
    } catch (error) {
        if (currentGeneration !== generation) return;
        viewer?.destroy();
        if (link.isConnected) {
            status.querySelector('[data-gallery-error]').textContent = labels.viewerError;
            status.hidden = false;
        }
    } finally {
        link.removeAttribute('aria-busy');
        if (currentGeneration === generation) opening = false;
    }
});

// delegated clicks also cover Livewire morphs; cancel pending opens when leaving.
document.addEventListener('livewire:navigating', () => {
    generation++;
    opening = false;
    viewer?.destroy();
});
